import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import { H3, Text } from '@/components/atoms'
import {
  FileUpload,
  InfoBanner,
  SelectedFileList,
  SignaturePad,
} from '@/components/molecules'
import { useClaimsPetStepTwo } from './page.claims.pet-step-two.hook'

interface ClaimsPetStepTwoPageProps {
  submitErrorKey?: string
  isOffline?: boolean
}

export function ClaimsPetStepTwoPage({
  submitErrorKey,
  isOffline,
}: ClaimsPetStepTwoPageProps) {
  const { t } = useTranslation()
  const {
    control,
    rules,
    fieldErrors,
    addReceipts,
    removeReceipt,
    selectPetPicture,
    petPicturePreviewUrl,
  } = useClaimsPetStepTwo()

  return (
    <div className="flex flex-col gap-6">
      <H3 className="text-[1.5rem] text-neutral-900">
        {t('claims.pet.stepTwo.title')}
      </H3>

      <Controller
        control={control}
        name="receipts"
        rules={rules.receipts}
        render={({ field }) => (
          <div className="flex flex-col gap-3">
            <FileUpload
              label={t('claims.pet.form.receiptLabel')}
              variant="document"
              multiple
              error={fieldErrors.receipts}
              onFilesSelect={addReceipts}
            />
            <SelectedFileList files={field.value} onRemove={removeReceipt} />
          </div>
        )}
      />

      <Controller
        control={control}
        name="petPicture"
        rules={rules.petPicture}
        render={() => (
          <FileUpload
            label={t('claims.pet.form.petPictureLabel')}
            previewUrl={petPicturePreviewUrl}
            error={fieldErrors.petPicture}
            onFileSelect={selectPetPicture}
          />
        )}
      />

      <div className="flex flex-col gap-3">
        <Text className="font-bold text-neutral-900">
          {t('claims.pet.form.pledgeTitle')}
        </Text>
        <Text className="text-sm text-neutral-600">
          {t('claims.pet.form.pledgeDescription')}
        </Text>
        <Controller
          control={control}
          name="signature"
          rules={rules.signature}
          render={({ field }) => (
            <SignaturePad
              label={t('claims.pet.form.signatureLabel')}
              error={fieldErrors.signature}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <InfoBanner message={t('claims.pet.form.reimbursementInfo')} />

      {submitErrorKey && (
        <div role="alert" className="alert alert-error">
          <span>{t(submitErrorKey)}</span>
        </div>
      )}

      {isOffline && (
        <Text className="text-indigo-base !text-sm">
          {t('payment.offlineError')}
        </Text>
      )}
    </div>
  )
}
